/**
 * Creating or editing a field DEPLOYMENT (21 Oct 2026): name, job, PM, dates, crew, site and an
 * explanation. One form for both, so the two cannot drift. The PM (27 Oct 2026) is pre-filled with
 * the job's PM when the job is chosen, and may be changed to anyone active, or to none. No rules here: the API decides who may,
 * refuses a crew member who is already committed, and says so in words, which this shows as given.
 *
 * Editing sends only what changed, with the version the page read; somebody else's edit since
 * comes back as a refusal to reload rather than a silent overwrite.
 */
import type { DeploymentDetail, DeploymentSaved, PersonRef, ProjectOption } from "@scheduler/shared";
import { useEffect, useMemo, useState, type FormEvent } from "react";

import { ApiError, useSchedulerApi } from "../api/scheduler";
import { ProjectPicker } from "../requests/ProjectPicker";

export function DeploymentForm({ editing, onSaved, onCancel }: {
  /** The deployment being edited, or absent to create one. */
  editing?: DeploymentDetail;
  onSaved: (saved: DeploymentSaved) => void;
  onCancel: () => void;
}) {
  const api = useSchedulerApi();
  const [people, setPeople] = useState<PersonRef[]>([]);
  const [projects, setProjects] = useState<ProjectOption[] | null>(null);
  const [projectsFailed, setProjectsFailed] = useState(false);

  const [title, setTitle] = useState(editing?.title ?? "");
  const [projectKey, setProjectKey] = useState<number | null>(editing?.project?.ajeraKey ?? null);
  // The PM by EE Key, as text for the <select>: "" is none.
  const [pmKey, setPmKey] = useState<string>(editing?.pm ? String(editing.pm.ajeraKey) : "");
  const [start, setStart] = useState(editing?.start ?? "");
  const [end, setEnd] = useState(editing?.end ?? "");
  const [crew, setCrew] = useState<string[]>(editing?.crew.map((p) => p.id) ?? []);
  const [site, setSite] = useState(editing?.site ?? "");
  const [explanation, setExplanation] = useState(editing?.explanation ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.people().then(setPeople, () => setPeople([]));
    if (!editing) api.projects().then(setProjects, () => setProjectsFailed(true));
  }, [api, editing]);

  const project = useMemo(() => projects?.find((p) => p.ajeraKey === projectKey) ?? null, [projects, projectKey]);
  // People on the crew stay listed even if they are no longer active, so a crew is never shown
  // shorter than it is.
  const listed = useMemo(() => {
    const extra = (editing?.crew ?? []).filter((c) => !people.some((p) => p.id === c.id));
    return [...people, ...extra].sort((a, b) => a.name.localeCompare(b.name));
  }, [people, editing]);
  // Who may be chosen as PM: everyone active, and the current PM even if they have since left, so
  // the box never shows a different PM from the one on record.
  const pmChoices = useMemo(() => {
    const current = editing?.pm && !people.some((p) => p.ajeraKey === editing.pm!.ajeraKey) ? [editing.pm] : [];
    return [...people, ...current].sort((a, b) => a.name.localeCompare(b.name));
  }, [people, editing]);

  /** Choosing a job pre-fills its PM, or none; the person can change it after. */
  function chooseProject(key: number | null): void {
    setProjectKey(key);
    const job = projects?.find((p) => p.ajeraKey === key);
    if (job) setPmKey(job.projectManager ? String(job.projectManager.ajeraKey) : "");
  }
  const pmValue = pmKey === "" ? null : Number(pmKey);

  const text = (v: string) => (v.trim() ? v.trim() : null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (editing) {
        const patch: Parameters<typeof api.updateDeployment>[1] = { version: editing.version };
        if (title.trim() !== editing.title) patch.title = title.trim();
        if (start !== editing.start) patch.start = start;
        if (end !== editing.end) patch.end = end;
        const was = editing.crew.map((p) => p.id).sort().join(",");
        if ([...crew].sort().join(",") !== was) patch.crewIds = crew;
        if (text(site) !== editing.site) patch.site = text(site);
        if (text(explanation) !== editing.explanation) patch.explanation = text(explanation);
        if (pmValue !== (editing.pm ? Number(editing.pm.ajeraKey) : null)) patch.pmAjeraKey = pmValue;
        onSaved(await api.updateDeployment(editing.id, patch));
      } else {
        if (projectKey === null) {
          setError("Choose the job this field work is for.");
          return;
        }
        onSaved(await api.createDeployment({
          title: title.trim(), projectAjeraKey: projectKey, start, end, crewIds: crew, site: text(site), explanation: text(explanation),
          pmAjeraKey: pmValue,
        }));
      }
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card card-pad deployform" onSubmit={(e) => void submit(e)}>
      <h3 style={{ marginTop: 0 }}>{editing ? "Change this deployment" : "New deployment"}</h3>

      <label className="field">
        <span className="label">Deployment name</span>
        <input type="text" value={title} maxLength={200} required onChange={(e) => setTitle(e.target.value)} />
      </label>

      <div className="field">
        <span className="label">Job</span>
        {editing ? (
          <span>
            {editing.project ? editing.project.name : <span className="muted">None (booked before jobs were required)</span>}
            <span className="hint"> A deployment keeps its job. For another job, book a new one.</span>
          </span>
        ) : (
          <ProjectPicker projects={projects} failed={projectsFailed} value={projectKey} onChange={chooseProject} />
        )}
      </div>

      <label className="field">
        <span className="label">PM <span className="muted">(optional)</span></span>
        <select value={pmKey} onChange={(e) => setPmKey(e.target.value)}>
          <option value="">No PM</option>
          {pmChoices.map((p) => <option key={p.ajeraKey} value={String(p.ajeraKey)}>{p.name}</option>)}
        </select>
        <span className="hint">
          {!editing && project
            ? project.projectManager
              ? `Filled in with the job's PM in Ajera, ${project.projectManager.name}. Change it if someone else runs this trip.`
              : "This job has no PM in Ajera. Choose one if someone runs this trip."
            : "Whoever created it, the PM and HR can change or cancel it."}
        </span>
      </label>

      <div className="deploydates">
        <label className="field">
          <span className="label">First day</span>
          <input type="date" value={start} required onChange={(e) => setStart(e.target.value)} />
        </label>
        <label className="field">
          <span className="label">Last day</span>
          <input type="date" value={end} required min={start || undefined} onChange={(e) => setEnd(e.target.value)} />
        </label>
      </div>

      <fieldset className="field">
        <legend className="label">Crew — {crew.length} on it, for the whole trip</legend>
        <div className="crewbox">
          {listed.map((p) => (
            <label key={p.id} className="crewrow">
              <input
                type="checkbox"
                checked={crew.includes(p.id)}
                onChange={(e) => setCrew(e.target.checked ? [...crew, p.id] : crew.filter((x) => x !== p.id))}
              />
              <span>{p.name}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="field">
        <span className="label">Site <span className="muted">(optional)</span></span>
        <input type="text" value={site} maxLength={200} onChange={(e) => setSite(e.target.value)} />
      </label>

      <label className="field">
        <span className="label">Explanation <span className="muted">(optional; seen by the crew, the PM, HR and whoever created it)</span></span>
        <textarea value={explanation} maxLength={2000} rows={3} onChange={(e) => setExplanation(e.target.value)} />
      </label>

      {error ? <p className="error" role="alert">{error}</p> : null}

      <div className="deployactions" style={{ marginTop: 12 }}>
        <button type="submit" className="primary narrow" disabled={busy}>
          {busy ? "Saving…" : editing ? "Save changes" : "Book deployment"}
        </button>
        <button type="button" className="ghost" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
